import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAll } from "../api/API";
import EventsRegistration from "../components/Event/EventsRegistration";
import RichTextRenderer from "../components/Generic/RichtextRenderer";
import ScrollEvents from "../components/Event/ScrollEvents";
import Reactshare from "../components/Generic/Reactshare";
import LoadingSpinner from "../components/Generic/LoadingSpinner";
import { FaRegClock, FaMapMarkerAlt } from "react-icons/fa";
import Seo from "../components/Generic/Seo";
import GoogleAnalytics from "../components/Generic/GoogleAnalytics";
import { toast } from "react-toastify";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { getCurrentUser, isLoggedIn } from "../utility/UserUtils";

const Events = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [attendeesVisible, setAttendeesVisible] = useState(false);

  const user = getCurrentUser(); // Current logged-in user
  const loggedIn = isLoggedIn(); // Check login status

  const getCohortId = (cohort) => {
    if (!cohort) return undefined;
    if (typeof cohort === "string") return cohort;
    if (typeof cohort === "object") return cohort.id || cohort._id || cohort;
    return undefined;
  };

  const getMemberId = (member) => {
    if (!member) return null;
    if (typeof member === "string") return member;
    if (typeof member === "object") return member.id || member._id || null;
    return null;
  };

  const resolveAvatar = (url) => {
    if (!url) return "/assets/images/default-avatar-v2.png";

    if (url.startsWith("http")) return url;

    return `${process.env.REACT_APP_API_URL}${url}`;
  };

  // Event status helpers
  const isEventOngoing = (start, end) => {
    const now = new Date();
    const s = new Date(start);
    const e = new Date(end);
    return now >= s && now <= e;
  };

  const isEventCompleted = (end) => new Date() > new Date(end);
  const isRegistrationClosed = (start) => {
    if (!start) return true;
    const diffMinutes =
      (new Date(start).getTime() - new Date().getTime()) / 60000;
    return diffMinutes <= 30;
  };

  const getDisabledReason = () => {
    if (!loggedIn) return "Please log in to register for this event.";
    if (isEventOngoing(event?.dateFrom, event?.dateTo))
      return "This event is currently ongoing.";
    if (isEventCompleted(event?.dateTo))
      return "This event has already been completed.";
    if (isRegistrationClosed(event?.dateFrom))
      return "Registration closes 30 minutes before the event starts.";
    if (
      event?.cohort &&
      getCohortId(user?.cohort) !== getCohortId(event?.cohort)
    )
      return "This event is restricted to your cohort.";
    if (event.capacity && event.attendeesCount >= event.capacity)
      return "This event has reached full capacity.";
    return "";
  };

  const canRegister =
    loggedIn &&
    user &&
    event?.status === "Upcoming" &&
    (!event?.cohort ||
      getCohortId(user?.cohort) === getCohortId(event?.cohort)) &&
    !isEventOngoing(event?.dateFrom, event?.dateTo) &&
    !isEventCompleted(event?.dateTo) &&
    !isRegistrationClosed(event?.dateFrom);

  const handleAttendClick = () => {
    if (!loggedIn) {
      toast.info("Please log in to attend this event.");
      navigate("/login");
    } else if (canRegister) {
      setShowModal(true);
    } else {
      toast.info(getDisabledReason());
    }
  };

  const handleViewAttendees = async () => {
    if (!loggedIn) {
      toast.info("Please log in to view attendees.");
      navigate("/login");
      return;
    }

    if (attendeesVisible) {
      setAttendeesVisible(false);
      return;
    }

    if (attendees.length > 0) {
      setAttendeesVisible(true);
      return;
    }

    try {
      setAttendeesLoading(true);
      const res = await getAll(`/public/events/${eventId}/attendees`);
      setAttendees(res.attendees || []);
      setAttendeesVisible(true);
    } catch (err) {
      toast.error("Failed to load attendees");
    } finally {
      setAttendeesLoading(false);
    }
  };

  useEffect(() => {
    async function fetchEvent() {
      try {
        const data = await getAll(`/public/events/${eventId}`);
        setEvent(data);
      } catch (err) {
        console.error("Error fetching event:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchEvent();
  }, [eventId]);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const formatTime = (time) =>
    new Date(time).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const extractText = (node) => {
    if (!node) return "";
    if (typeof node.text === "string") return node.text;
    if (Array.isArray(node.children))
      return node.children.map(extractText).join(" ");
    return "";
  };

  const seoData = {
    title: event?.name
      ? `${event.name} | WiSE Events — Women in Semiconductor Ecosystem`
      : "Event Details | WiSE Events — Women in Semiconductor Ecosystem",
    keywords:
      typeof event?.keywords === "string" && event.keywords.trim() !== ""
        ? event.keywords
        : "Explore upcoming and past events by WiSE: seminars, webinars, workshops, networking sessions, and more designed to empower and connect women across the the semiconductor ecosystem.",
    pathSlug: event?.eventId ? `events/${event.eventId}` : "events",
    description:
      extractText(event?.description?.root) ||
      "WiSE events, semiconductor events for women, women in semiconductor workshops, WiSE webinars, women in tech events, semiconductor networking events, women in STEM events, semiconductor industry events for women",
  };

  if (loading)
    return (
      <div className="my-5" style={{ minHeight: "400px" }}>
        <LoadingSpinner />
      </div>
    );
  if (!event) return <p className="text-center mt-5">Event not found.</p>;

  return (
    <>
      <Seo data={seoData} />
      <GoogleAnalytics page={seoData.pathSlug} title={seoData.title} />

      {/* Sticky Header */}
      <div
        className="w-100 mb-5 bg-primary text-white p-4 sticky-lg-top"
        style={{ zIndex: 10 }}
      >
        <h2 className="mb-3 ms-5">{event?.name}</h2>

        {/* Organizers */}
        {event.organizers?.length > 0 && (
          <div className="d-flex align-items-center gap-4 ms-5 flex-wrap">
            {event.organizers.map((org, idx) => {
              const member = org;
              const firstName = member?.firstName || "";
              const lastName = member?.lastName || "";
              const avatarUrl = member.avatarUrl;

              return (
                <div
                  key={idx}
                  className="d-flex align-items-center gap-2"
                  style={{ cursor: member ? "pointer" : "default" }}
                  onClick={() => {
                    const memberId = getMemberId(member);
                    if (!memberId) {
                      // console.error(" Organizer member ID missing:", member);
                      return;
                    }
                    navigate(`/members/${memberId}`);
                  }}
                  title={member ? "View profile" : ""}
                >
                  {avatarUrl ? (
                    <img
                      src={resolveAvatar(member.avatarUrl)}
                      alt={`${firstName} ${lastName}`}
                      className="rounded-circle"
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      className="bg-secondary rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "60px", height: "60px", color: "white" }}
                    >
                      {firstName?.[0] || "?"}
                    </div>
                  )}
                  <div className="text-start">
                    <p
                      className="mb-0 fw-light"
                      style={{ fontSize: "0.85rem" }}
                    >
                      Organizer
                    </p>
                    <h6 className="mb-0" style={{ fontSize: "0.9rem" }}>
                      {firstName} {lastName}
                    </h6>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-3">
        {/* Event Image + Info */}
        <div className="row align-items-start g-4 mx-0 mt-5">
          <div className="col-lg-5 col-12">
            {event.image?.url && (
              <img
                src={event.image.url}
                alt={event.name}
                className="rounded w-100"
                style={{ objectFit: "contain", height: "300px" }}
              />
            )}
          </div>

          <div className="col-lg-7 col-12">
            <h1 className="wise-secondary-gradient mb-3">{event.name}</h1>
            <h2 className="text-secondary d-flex align-items-center gap-3 mt-3">
              <FaRegClock size={35} />
              {formatDate(event.dateFrom) === formatDate(event.dateTo)
                ? formatDate(event.dateFrom)
                : `${formatDate(event.dateFrom)} → ${formatDate(event.dateTo)}`}
              , {formatTime(event.startTime)} - {formatTime(event.endTime)}
            </h2>

            <h2 className="text-secondary d-flex align-items-center gap-3 mt-3">
              <FaMapMarkerAlt size={35} />

              {event.eventType === "online" ? (
                <a
                  href={event.eventLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary"
                >
                  Online Event (Click to join)
                </a>
              ) : event.venue ? (
                event.venue
              ) : (
                "Venue not available"
              )}
            </h2>
            <h2 className="text-secondary mt-3">
              Price: {event.price === 0 ? "Free" : `₹${event.price}`}
            </h2>

            {/* Share Component */}
            <Reactshare
              url={window.location.href}
              title={event.name}
              text="Check out this event!"
            />
          </div>
        </div>

        {/* Event Details */}
        <div className="row g-4 mx-0 mt-5">
          <div className="col-lg-5 d-none d-lg-block">
            <h4 className="fw-bold text-center mb-4 text-secondary">
              Browse Upcoming Events
            </h4>
            <div
              className="border rounded p-1 overflow-auto"
              style={{ maxHeight: "700px" }}
            >
              <ScrollEvents selectedEventId={eventId} />
            </div>
          </div>

          <div className="col-lg-7 col-12">
            <h4 className="text-secondary mb-3">Details</h4>
            <div className="border rounded p-3 mb-4">
              {/* {event.description?.root?.children && (
                <RichTextRenderer
                  content={event.description}
                  linkTarget="_blank"
                />
              )} */}
              {event.description?.root?.children?.length > 0 ? (
                <RichTextRenderer
                  content={event.description.root.children}
                  linkTarget="_blank"
                />
              ) : (
                <p className="text-muted">No description available</p>
              )}
            </div>

            {/* Attendees */}
            <div className="mt-4">
              {/* Attendees count */}
              <h5 className="text-secondary mb-2">
                Attendees ({event.attendeesCount ?? 0})
              </h5>

              {/* View / Hide button */}
              <button
                className="btn btn-outline-secondary mb-3"
                onClick={handleViewAttendees}
              >
                {attendeesVisible ? "Hide Attendees" : "View Attendees"}
              </button>

              {/* Loading */}
              {attendeesLoading && (
                <LoadingSpinner text="Loading attendees..." />
              )}

              {/* Attendees list */}
              {attendeesVisible && attendees.length > 0 && (
                <div
                  className="d-flex overflow-auto pb-2"
                  style={{ gap: "15px" }}
                >
                  {attendees.map((att, idx) => {
                    const member = att.member;
                    if (!member) return null;

                    return (
                      <div
                        key={idx}
                        className="border rounded p-2 text-center"
                        style={{ minWidth: "100px", cursor: "pointer" }}
                        onClick={() => navigate(`/members/${member.id}`)}
                      >
                        <img
                          src={resolveAvatar(member.avatarUrl)}
                          alt={`${member.firstName} ${member.lastName}`}
                          className="rounded-circle mb-2"
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                          }}
                        />
                        <p className="text-secondary mb-0 small fw-medium">
                          {member.firstName} {member.lastName}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Empty state */}
              {attendeesVisible &&
                !attendeesLoading &&
                attendees.length === 0 && (
                  <p className="text-muted">No attendees yet.</p>
                )}
            </div>
          </div>
        </div>

        {/* Footer with buttons */}
        <div className="row mx-0">
          <div className="col-12">
            <div
              className="d-flex justify-content-between align-items-center border-top p-3 bg-white fixed-bottom"
              style={{ zIndex: 1050, boxShadow: "0 -2px 6px rgba(0,0,0,0.1)" }}
            >
              <div className="text-start">
                <p className="mb-1 text-dark fw-medium">
                  {formatDate(event.dateFrom) === formatDate(event.dateTo)
                    ? formatDate(event.dateFrom)
                    : `${formatDate(event.dateFrom)} → ${formatDate(
                        event.dateTo,
                      )}`}
                  , {formatTime(event.startTime)} - {formatTime(event.endTime)}
                </p>
                <h5 className="mb-0 text-dark">
                  {event.name || "Event Title"}
                </h5>
              </div>

              <div className="d-flex gap-3 align-items-center">
                <button
                  onClick={() =>
                    navigator.share
                      ? navigator
                          .share({
                            title: event.name,
                            text: "Check out this event!",
                            url: window.location.href,
                          })
                          .catch((err) => console.error("Error sharing:", err))
                      : alert("Sharing not supported on this browser.")
                  }
                  className="btn btn-outline-primary px-4"
                >
                  Share
                </button>

                {canRegister ? (
                  <button
                    className="btn btn-primary px-4"
                    onClick={handleAttendClick}
                  >
                    Attend
                  </button>
                ) : (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-disabled">
                        {getDisabledReason()}
                      </Tooltip>
                    }
                  >
                    <span className="d-inline-block">
                      <button
                        className="btn btn-secondary px-4"
                        disabled
                        style={{ pointerEvents: "none" }}
                      >
                        Attend
                      </button>
                    </span>
                  </OverlayTrigger>
                )}
              </div>

              {showModal && user && (
                <EventsRegistration
                  show={showModal}
                  onHide={() => setShowModal(false)}
                  event={event}
                  onRegistered={(member) => {
                    setEvent((prev) => {
                      if (!prev) return prev;

                      return {
                        ...prev,
                        attendees: [...(prev.attendees || []), { member }],
                        attendeesCount: (prev.attendeesCount ?? 0) + 1,
                      };
                    });
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Events;
